import type { ChargerState } from '@/api/stations';

interface Props {
  state: ChargerState | string | null;
}

interface Style {
  label: string;
  dot: string;
  text: string;
  ring: string;
  pulse?: boolean;
}

function styleFor(state: ChargerState | string | null): Style {
  switch (state) {
    case 'AVAILABLE':
      return {
        label: 'Disponível',
        dot: 'bg-bow-green',
        text: 'text-bow-green',
        ring: 'border-bow-green/30 bg-bow-green/5'
      };
    case 'PREPARING':
      return {
        label: 'Preparando',
        dot: 'bg-bow-yellow',
        text: 'text-bow-orange',
        ring: 'border-bow-yellow/30 bg-bow-yellow/10',
        pulse: true
      };
    case 'CHARGING':
      return {
        label: 'Carregando',
        dot: 'bg-bow-blue',
        text: 'text-bow-blue',
        ring: 'border-bow-blue/30 bg-bow-blue/10',
        pulse: true
      };
    case 'SUSPENDED_EV':
      return {
        label: 'Pausado pelo carro',
        dot: 'bg-bow-yellow',
        text: 'text-bow-orange',
        ring: 'border-bow-yellow/30 bg-bow-yellow/10'
      };
    case 'SUSPENDED_EVSE':
      return {
        label: 'Pausado pelo carregador',
        dot: 'bg-bow-yellow',
        text: 'text-bow-orange',
        ring: 'border-bow-yellow/30 bg-bow-yellow/10'
      };
    case 'FINISHING':
      return {
        label: 'Finalizando',
        dot: 'bg-bow-sky',
        text: 'text-bow-sky',
        ring: 'border-bow-sky/30 bg-bow-sky/10',
        pulse: true
      };
    case 'FAULTED':
      return {
        label: 'Com falha',
        dot: 'bg-bow-red',
        text: 'text-bow-red',
        ring: 'border-bow-red/30 bg-bow-red/10'
      };
    case 'RESERVED':
      return {
        label: 'Reservado',
        dot: 'bg-bow-purple',
        text: 'text-bow-purple',
        ring: 'border-bow-purple/30 bg-bow-purple/10'
      };
    case 'UNAVAILABLE':
      // Sinalizado pelo backend quando a station tem `active_session_id` —
      // alguma sessão (Bow ou outro tenant) ocupa o charger e o user não pode
      // iniciar uma nova em paralelo.
      return {
        label: 'Em uso',
        dot: 'bg-bow-royal/40',
        text: 'text-bow-royal/60',
        ring: 'border-bow-royal/15 bg-bow-royal/5'
      };
    case 'OFFLINE':
      return {
        label: 'Indisponível',
        dot: 'bg-bow-royal/40',
        text: 'text-bow-royal/60',
        ring: 'border-bow-royal/15 bg-bow-royal/5'
      };
    default:
      return {
        label: 'Status indisponível',
        dot: 'bg-bow-royal/30',
        text: 'text-bow-royal/50',
        ring: 'border-bow-royal/10 bg-bow-royal/5'
      };
  }
}

export function ChargerStatusBadge({ state }: Props) {
  const s = styleFor(state);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-black ${s.ring} ${s.text}`}
      role="status"
      aria-label={`Estado do carregador: ${s.label}`}
    >
      <span className={`relative flex h-2 w-2`} aria-hidden>
        {s.pulse && (
          <span
            className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${s.dot}`}
          />
        )}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${s.dot}`} />
      </span>
      {s.label}
    </span>
  );
}

/** Conector é "selecionável" quando o user pode iniciar uma sessão:
 * - AVAILABLE: pronto pra plugar e começar.
 * - PREPARING: cabo já plugado pelo user, momento certo de mandar RemoteStart.
 *   (Backend marca como UNAVAILABLE quando o PREPARING vem de outra sessão.)
 */
export function isChargerSelectable(state: ChargerState | string | null): boolean {
  return state === 'AVAILABLE' || state === 'PREPARING';
}
